import react, { useState, useEffect, useRef } from "react";
import { updateTweet } from "../../services/tweetService";
import { useToast } from "../../provider/ToastContext";
import {
  Copy,
  Pencil,
  Twitter,
  AtSign,
  Wand2,
  Scissors,
  Sparkles,
  Hash,
  Check,
} from "lucide-react";

const GeneratedPosts = ({
  generatedTweets,
  setGeneratedTweets,
  editingIndex,
  setEditingIndex,
}) => {
  /* -------- ACTIONS -------- */

  const [copiedIndex, setCopiedIndex] = useState(null);
  const [draftText, setDraftText] = useState("");
  const [savingIndex, setSavingIndex] = useState(null);
  const { showToast } = useToast();

  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
    }
  }, [editingIndex]);

  const copyTweet = (text, index) => {
    navigator.clipboard.writeText(text);

    setCopiedIndex(index);

    setTimeout(() => {
      setCopiedIndex(null);
    }, 1500);
  };

  const shareTweet = (text) => {
    const url =
      "https://twitter.com/intent/tweet?text=" + encodeURIComponent(text);
    window.open(url, "_blank");
  };

  const shareThreads = (text) => {
    const url =
      "https://www.threads.net/intent/post?text=" + encodeURIComponent(text);
    window.open(url, "_blank");
  };

  const editTweet = (index, value) => {
    const updated = [...generatedTweets];
    updated[index] = { ...updated[index], text: value };
    setGeneratedTweets(updated);
  };

  const applyToolGenerated = (index, type) => {
    const updated = [...generatedTweets];
    let text = updated[index].text;

    if (type === "rewrite") text = text;
    if (type === "shorten") text = text.slice(0, 200);
    if (type === "hook") text = "🔥 " + text;
    if (type === "hashtags") text += "\n\n#BuildInPublic #Startups";

    updated[index] = { ...updated[index], text };
    setGeneratedTweets(updated);
  };

  return (
    <div className="bg-white border rounded-xl p-6">
      <h3 className="text-sm font-semibold mb-4">Generated Posts</h3>

      {generatedTweets.length === 0 && (
        <p className="text-xs text-gray-400">
          Generated posts will appear here.
        </p>
      )}

      <div className="space-y-4">
        {generatedTweets.map((t, i) => (
          <div key={i} className="border rounded-lg p-4 text-sm">
            {/* HEADER */}
            <div className="flex justify-between mb-2">
              <span className="text-xs text-gray-400">
                {i + 1}/{generatedTweets.length} • {t.text.length}/280
              </span>

              <div className="flex gap-3">
                {/* COPY */}
                <div className="relative group">
                  <button onClick={() => copyTweet(t.text, i)}>
                    {copiedIndex === i ? (
                      <Check size={16} className="text-green-500" />
                    ) : (
                      <Copy size={16} />
                    )}
                  </button>

                  <span
                    className="absolute -top-8 left-1/2 -translate-x-1/2 
    bg-gray-900 text-white text-xs px-2 py-1 rounded 
    opacity-0 group-hover:opacity-100 transition whitespace-nowrap"
                  >
                    {copiedIndex === i ? "Copied!" : "Copy"}
                  </span>
                </div>

                {/* EDIT */}
                <div className="relative group">
                  <button
                    onClick={() => {
                      setEditingIndex(i);
                      setDraftText(t.text); // load current text into draft
                    }}
                  >
                    <Pencil size={16} />
                  </button>

                  <span
                    className="absolute -top-8 left-1/2 -translate-x-1/2 
    bg-gray-900 text-white text-xs px-2 py-1 rounded 
    opacity-0 group-hover:opacity-100 transition whitespace-nowrap"
                  >
                    Edit
                  </span>
                </div>

                {/* TWITTER */}
                <div className="relative group">
                  <button onClick={() => shareTweet(t.text)}>
                    <Twitter size={16} />
                  </button>
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition whitespace-nowrap">
                    Tweet
                  </span>
                </div>

                {/* THREADS */}
                <div className="relative group">
                  <button onClick={() => shareThreads(t.text)}>
                    <AtSign size={16} />
                  </button>
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition whitespace-nowrap">
                    Thread
                  </span>
                </div>
              </div>
            </div>

            {/* TOOLS */}
            <div className="flex gap-2 mb-2 flex-wrap">
              <button
                onClick={() => applyToolGenerated(i, "rewrite")}
                className="text-xs bg-gray-100 px-2 py-1 rounded flex items-center gap-1 hover:bg-gray-200"
              >
                <Wand2 size={12} /> Rewrite
              </button>

              <button
                onClick={() => applyToolGenerated(i, "shorten")}
                className="text-xs bg-gray-100 px-2 py-1 rounded flex items-center gap-1 hover:bg-gray-200"
              >
                <Scissors size={12} /> Shorten
              </button>

              <button
                onClick={() => applyToolGenerated(i, "hook")}
                className="text-xs bg-gray-100 px-2 py-1 rounded flex items-center gap-1 hover:bg-gray-200"
              >
                <Sparkles size={12} /> Hook
              </button>

              <button
                onClick={() => applyToolGenerated(i, "hashtags")}
                className="text-xs bg-gray-100 px-2 py-1 rounded flex items-center gap-1 hover:bg-gray-200"
              >
                <Hash size={12} /> Hashtags
              </button>
            </div>

            {/* CONTENT */}
            {editingIndex === i ? (
              <div className="space-y-2">
                {/* <textarea
                  value={draftText}
                  onChange={(e) => setDraftText(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                /> */}
                <textarea
                  ref={textareaRef}
                  value={draftText}
                  onChange={(e) => {
                    setDraftText(e.target.value);

                    const el = e.target;
                    el.style.height = "auto";
                    el.style.height = el.scrollHeight + "px";
                  }}
                  rows={1}
                  className="w-full border rounded-lg px-3 py-2 resize-none overflow-hidden"
                />

                <div className="flex justify-end gap-2">
                  {/* CANCEL */}
                  <button
                    onClick={() => {
                      setEditingIndex(null);
                      setDraftText("");
                    }}
                    className="px-3 py-1 text-xs rounded-lg border hover:bg-gray-100"
                  >
                    Cancel
                  </button>

                  {/* SAVE */}
                  <button
                    disabled={savingIndex === i}
                    onClick={async () => {
                      const tweet = generatedTweets[i];

                      if (!draftText.trim()) {
                        showToast("Tweet cannot be empty", "error");
                        return;
                      }

                      setSavingIndex(i);

                      try {
                        await updateTweet(tweet.tweet_id, draftText);

                        const updated = [...generatedTweets];
                        updated[i] = { ...updated[i], text: draftText };
                        setGeneratedTweets(updated);

                        setEditingIndex(null);
                        setDraftText("");

                        showToast("Tweet updated!", "success");
                      } catch {
                        showToast("Failed to update tweet", "error");
                      }

                      setSavingIndex(null);
                    }}
                    className={`px-3 py-1 text-xs rounded-lg text-white ${
                      savingIndex === i
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-indigo-600 hover:bg-indigo-700"
                    }`}
                  >
                    {savingIndex === i ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            ) : (
              <p className="whitespace-pre-line text-gray-800">{t.text}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default GeneratedPosts;
